'use client';

import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { faCircle, faClose, faSearch } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatName } from '@pickleballinc/lib/format-name';
import { PlayerSelectCard, PlayerSelectCardSkeleton } from '@pickleballinc/ui/components/global/Cards/PlayerSelectCard';
import { Path } from '@pickleballinc/ui/components/team-leagues-movements/_components/path';
import { Step } from '@pickleballinc/ui/components/team-leagues-movements/_components/step';
import { useDebounce } from '@pickleballinc/ui/components/team-leagues-movements/utils/use-debounce';
import { Avatar, AvatarFallback, AvatarImage } from '@pickleballinc/ui/components/ui/avatar';
import { Button } from '@pickleballinc/ui/components/ui/button';
import { Checkbox } from '@pickleballinc/ui/components/ui/checkbox';
import { Icon } from '@pickleballinc/ui/components/ui/icon';
import { Panel, PanelContent, PanelHeader, PanelBody, PanelClose, PanelTitle, PanelDescription } from '@pickleballinc/ui/components/ui/panel';
import { RadioGroup, RadioGroupItem } from '@pickleballinc/ui/components/ui/radio-group';
import { cn } from '@pickleballinc/ui/lib/utils';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import { useState } from 'react';
import { toast } from 'react-toastify';

import { subStepContent } from '@/components/constants/constants';
import { DecoratedInput } from '@/components/organisms/DecoratedInput';
import { getSubCandidateCardProps, getSubPlayerCardBaseProps, getSubRating } from '@/components/organisms/ScoreCards/subs/subPlayerCardProps';
import { UserCard } from '@/components/organisms/ScoreCards/subs/UserCard';
import { Matchup, Match, TeamLeaguePlayerInfo } from '@/entities';
import { TieBreakerFormatType } from '@/entities/Matchup';
import { RotationPlayerInfo, RotationsInfo } from '@/entities/rotations/RotationsInfo';
import { Country } from '@/entities/shared/lookups/Country';
import { State } from '@/entities/shared/lookups/State';
import { Photo } from '@/entities/shared/user/Photo';
import { User } from '@/entities/SystemUsers';
import { useTeamLeagueInfo } from '@/hooks/custom-hooks/useTeamLeagueInfo';
import { useSystemUsersSearchQuery } from '@/hooks/hooks';
import { matchKeys } from '@/hooks/matches/matchKeys';
import { useUpdateMatchMutation } from '@/hooks/matches/useUpdateMatch';
import { useMatchupSettingsQuery } from '@/hooks/matchup/useMatchupSettingsQuery';
import { useTieBreakerMutation } from '@/hooks/tie-breaker/useRotationsMutation';
import { useTieBreakerMatch } from '@/hooks/tie-breaker/useTieBreakerMatch';

export interface Rotation {
	uuid: string;
	matchUuid: string;
	playerOneUuid: string;
	playerTwoUuid?: string;
	rotationOrder: number;
	team: number;
}

interface SubPlayerModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	type: 'team' | 'event' | 'users';
	rotationsInfo?: RotationsInfo;
	teamOnePlayers: TeamLeaguePlayerInfo[];
	teamTwoPlayers: TeamLeaguePlayerInfo[];
	teamOneTitle: string;
	teamTwoTitle: string;
	countries: Country[];
	states: State[];
	substitutePlayers: TeamLeaguePlayerInfo[];
	matches: Match[];
	matchupUuid: string;
	matchupData?: Matchup;
}

export const determinePlayerPhotoUrl = (photo?: Photo) => {
	if (!photo) return undefined;
	const keys: (keyof Photo)[] = [
		'headshot',
		'fullImage',
		'featuredImage',
		'overviewImage',
		'profileImage',
		'headerImage',
		'featuredUserImage',
		'actionUserImage'
	];

	for (const key of keys) {
		if (photo[key]?.url) {
			return photo[key]!.url;
		}
	}

	return undefined;
};

export const SubPlayerModal = ({
	open,
	onOpenChange,
	type,
	teamOnePlayers,
	teamTwoPlayers,
	teamOneTitle,
	teamTwoTitle,
	countries,
	states,
	matches,
	substitutePlayers,
	rotationsInfo,
	matchupUuid,
	matchupData
}: SubPlayerModalProps) => {
	const queryClient = useQueryClient();

	const [isLoadingSubmit, setIsLoading] = useState(false);
	const [step, setStep] = useState(1);
	const [subbedOut, setSubbedOut] = useState<TeamLeaguePlayerInfo>();
	const [team, setTeam] = useState<1 | 2>();
	const [subbedIn, setSubbedIn] = useState<TeamLeaguePlayerInfo | User>();
	const searchParams = useSearchParams();
	const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
	const debouncedSearch = useDebounce(searchTerm, 700);
	const [selectedMatches, setSelectedMatches] = useState<string[]>([]);
	const { showPlayerRatingsSingles, showPlayerRatingsDoubles } = useTeamLeagueInfo();

	const { data: usersResponse, isLoading: isLoadingUsers, error } = useSystemUsersSearchQuery(debouncedSearch);

	const { mutate: changePlayerMutation } = useUpdateMatchMutation({
		onSuccessCallback: () => {
			toast.success('Successfully updated');
		}
	});

	const { id: tieBreakerMatchId } = useTieBreakerMatch(matchupData);
	const { createRotation } = useTieBreakerMutation(tieBreakerMatchId);

	const { data: matchupSettings } = useMatchupSettingsQuery(matchupData?.uuid!);

	const handleCheckboxChange = (matchId: string, isChecked: boolean) => {
		setSelectedMatches((prev) => (isChecked ? [...prev, matchId] : prev.filter((id) => id !== matchId)));
	};

	const teamOneIDs: string[] = [];
	const teamTwoIDs: string[] = [];

	matches?.forEach((match) => {
		if (match.teamOnePlayerOneUuid) {
			teamOneIDs.push(match.teamOnePlayerOneUuid);
		}
		if (match.teamOnePlayerTwoUuid) {
			teamOneIDs.push(match.teamOnePlayerTwoUuid);
		}

		if (match.teamTwoPlayerOneUuid) {
			teamTwoIDs.push(match.teamTwoPlayerOneUuid);
		}
		if (match.teamTwoPlayerTwoUuid) {
			teamTwoIDs.push(match.teamTwoPlayerTwoUuid);
		}
	});

	rotationsInfo?.teamOne.forEach((rotation) => {
		if (rotation.playerOneUuid) {
			teamOneIDs.push(rotation.playerOneUuid);
		}
		if (rotation.playerTwoUuid) {
			teamOneIDs.push(rotation.playerOneUuid);
		}
	});

	rotationsInfo?.teamTwo.forEach((rotation) => {
		if (rotation.playerOneUuid) {
			teamTwoIDs.push(rotation.playerOneUuid);
		}
		if (rotation.playerTwoUuid) {
			teamTwoIDs.push(rotation.playerOneUuid);
		}
	});

	const filteredTeamOnePlayers: TeamLeaguePlayerInfo[] = teamOnePlayers.filter((player) => teamOneIDs.includes(player.userUuid));
	const filteredTeamTwoPlayers: TeamLeaguePlayerInfo[] = teamTwoPlayers.filter((player) => teamTwoIDs.includes(player.userUuid));

	const closeModal = () => {
		setStep(1);
		setSubbedIn(undefined);
		setSubbedOut(undefined);
		setSelectedMatches([]);
		onOpenChange(false);
	};

	const handleNext = () => {
		if (step === 1) setStep(2);
		else if (step === 2) setStep(3);
		else if (step === 3) setStep(4);
		else if (step === 4) submitSubs();
	};

	const handleBack = () => {
		if (step === 4) setStep(3);
		else if (step === 3) setStep(2);
		else if (step === 2) setStep(1);
		else if (step === 1) closeModal();
	};

	const getEligibleMatches = (allMatches: Match[], subbedOutUuid: string, team: number) => {
		return allMatches.filter((match) => {
			const playsInLineup =
				match.teamOnePlayerOneUuid === subbedOutUuid ||
				match.teamOnePlayerTwoUuid === subbedOutUuid ||
				match.teamTwoPlayerOneUuid === subbedOutUuid ||
				match.teamTwoPlayerTwoUuid === subbedOutUuid;

			if (playsInLineup) return true;

			if (match.isTieBreaker) {
				const teamKey = team === 1 ? 'teamOne' : 'teamTwo';
				const teamRotations = rotationsInfo?.[teamKey] || [];

				const playsInRotation = teamRotations.some(
					(rotation: RotationPlayerInfo) => rotation.playerOneUuid === subbedOutUuid || rotation.playerTwoUuid === subbedOutUuid
				);

				return playsInRotation;
			}

			return false;
		});
	};

	const submitSubs = async () => {
		setIsLoading(true);

		matches
			.filter((match) => selectedMatches.includes(match.matchUuid))
			.map((match) => {
				const subOut = subbedOut?.userUuid;
				const subIn = subbedIn && 'userUuid' in subbedIn ? subbedIn.userUuid : subbedIn?.uuid;
				const teamKey = team === 1 ? 'teamOne' : 'teamTwo';

				const requestPayload: { matchId: string; updateData: any } = {
					matchId: match.matchUuid,
					updateData: {
						lineupChangeIsSubstitution: true
					}
				};

				let shouldCallLineupEP = true;

				if (match.teamOnePlayerOneUuid === subOut) {
					requestPayload.updateData.swappedOutButPlayedTeam1UserId1 = subIn;
				} else if (match.teamOnePlayerTwoUuid === subOut) {
					requestPayload.updateData.swappedOutButPlayedTeam1UserId2 = subIn;
				} else if (match.teamTwoPlayerOneUuid === subOut) {
					requestPayload.updateData.swappedOutButPlayedTeam2UserId1 = subIn;
				} else if (match.teamTwoPlayerTwoUuid === subOut) {
					requestPayload.updateData.swappedOutButPlayedTeam2UserId2 = subIn;
				} else {
					shouldCallLineupEP = false;
				}

				if (shouldCallLineupEP) {
					changePlayerMutation(requestPayload, {
						onSuccess: () => {
							queryClient.invalidateQueries({
								queryKey: matchKeys.matches(matchupUuid)
							});
						}
					});
				}

				if (match.isTieBreaker) {
					const teamRotations = rotationsInfo?.[teamKey];
					const rotationsToUpdate: Rotation[] = [];

					let hasChanges = false;

					teamRotations?.forEach((rotation: RotationPlayerInfo) => {
						const newPlayerOneUuid = rotation.playerOneUuid === subOut ? subIn : rotation.playerOneUuid;
						const newPlayerTwoUuid = rotation.playerTwoUuid === subOut ? subIn : rotation.playerTwoUuid;

						if (newPlayerOneUuid !== rotation.playerOneUuid || newPlayerTwoUuid !== rotation.playerTwoUuid) {
							hasChanges = true;
						}

						if (matchupSettings?.data?.tieBreakerFormat.title === TieBreakerFormatType.DOUBLES) {
							rotationsToUpdate.push({
								uuid: rotation.rotationUuid,
								matchUuid: match.matchUuid,
								playerOneUuid: newPlayerOneUuid ?? '',
								playerTwoUuid: newPlayerTwoUuid ?? '',
								rotationOrder: rotation.rotationOrder,
								team: team!
							});
						} else {
							rotationsToUpdate.push({
								uuid: rotation.rotationUuid,
								matchUuid: match.matchUuid,
								playerOneUuid: newPlayerOneUuid ?? '',
								rotationOrder: rotation.rotationOrder,
								team: team!
							});
						}
					});

					if (hasChanges) {
						createRotation.mutate({ rotations: rotationsToUpdate });
					}
				}
			});

		closeModal();
		setIsLoading(false);
	};

	const isNextStepDisabled = () => {
		return (step === 1 && !subbedOut) || (step === 2 && !subbedIn) || (step === 3 && selectedMatches.length === 0);
	};

	const renderPlayer = (player: TeamLeaguePlayerInfo) => {
		const isSelected = subbedIn?.uuid === player.uuid;

		return (
			<PlayerSelectCard
				key={player.uuid}
				{...getSubCandidateCardProps(player, determinePlayerPhotoUrl(player.photo), states, countries)}
				ratings={[
					{ label: 'D', value: player.ratingDoubles },
					{ label: 'S', value: player.ratingSingles }
				]}
				selected={isSelected}
				action={
					isSelected
						? { label: 'Selected', onClick: () => {}, disabled: true, variant: 'muted' }
						: { label: 'Select', onClick: () => setSubbedIn(player) }
				}
			/>
		);
	};

	const renderSub = (player: TeamLeaguePlayerInfo | User, onEdit: () => void) => {
		// A roster player carries `ratingDoubles` / `ratingSingles`, a searched user `doublesRating` / `singlesRating`.
		const ratingSets = [
			'ratingDoubles' in player && 'ratingSingles' in player ? { doubles: player.ratingDoubles, singles: player.ratingSingles } : null,
			'doublesRating' in player && 'singlesRating' in player ? { doubles: player.doublesRating, singles: player.singlesRating } : null
		];
		const ratings = ratingSets.flatMap((set) =>
			set
				? [
						...(showPlayerRatingsDoubles ? [getSubRating('D', set.doubles)] : []),
						...(showPlayerRatingsSingles ? [getSubRating('S', set.singles)] : [])
					]
				: []
		);

		return (
			<PlayerSelectCard
				{...getSubPlayerCardBaseProps(player, 'photo' in player ? determinePlayerPhotoUrl(player.photo) : player.profilePictureUrl)}
				ratings={ratings}
				actionSlot={
					<Button
						className="text-xs font-semibold leading-[18px] !px-2 !py-1.5 !h-fit !rounded-md"
						onClick={() => onEdit()}
						variant="outline"
					>
						Edit
					</Button>
				}
			/>
		);
	};

	return (
		<Panel open={open} onOpenChange={onOpenChange}>
			<PanelContent className="w-530 flex w-full flex-col" aria-describedby="court-assignment-description">
				<PanelHeader className="flex flex-col border-b border-gray-200 px-6 pb-2 pt-4">
					<div className="flex items-center justify-between">
						<PanelTitle asChild>
							<h2 className="text-2xl font-semibold text-gray-900">{subStepContent[step]?.title}</h2>
						</PanelTitle>
						<PanelClose asChild>
							<button
								className="flex size-8 items-center justify-center rounded-full bg-gray-100 transition-colors hover:bg-gray-200"
								aria-label="Close dialog"
							>
								<FontAwesomeIcon icon={faXmark} className="size-5 text-gray-700" />
							</button>
						</PanelClose>
					</div>
					<PanelDescription className="text-start text-base font-normal text-gray-600">
						{subStepContent[step]?.description}
					</PanelDescription>
				</PanelHeader>

				<PanelBody className="flex-1 px-6 pb-8 pt-4">
					<div className="flex h-full flex-col gap-4">
						<div className="mb-12 flex w-full items-center justify-between px-6 transition-all">
							<Step text="Subbed out" active={step === 1} finished={step > 1} onClick={() => setStep(1)} isOrange />
							<Path active={step > 1} isOrange />
							<Step
								text="Subbed in"
								active={step === 2}
								finished={step > 2}
								onClick={() => !isNextStepDisabled() && setStep(2)}
								isOrange
							/>
							<Path active={step > 2} isOrange />
							<Step
								text="Match & Games"
								active={step === 3}
								finished={step > 3}
								onClick={() => !isNextStepDisabled() && setStep(3)}
								isOrange
							/>
							<Path active={step > 3} isOrange />
							<Step text="Summary" active={step === 4} finished={step > 4} onClick={() => {}} isOrange />
						</div>
						{step === 1 && (
							<div className="flex flex-col gap-4">
								<p className="text-lg font-semibold text-gray-900">{teamTwoTitle}</p>
								<RadioGroup
									className="rounded-lg border border-gray-200"
									value={subbedOut?.userUuid}
									onValueChange={(value) => {
										const player = filteredTeamTwoPlayers.find((player) => player.uuid === value);
										setSubbedOut(player);
										setTeam(2);
									}}
								>
									{filteredTeamTwoPlayers.map((player, index) => (
										<div
											key={player.uuid}
											className={`flex items-center gap-4 px-4 py-3 ${index % 2 === 1 ? 'bg-gray-100' : ''} ${index === teamTwoPlayers.length - 1 ? 'rounded-lg' : ''}`}
										>
											<RadioGroupItem
												id={player.uuid}
												value={player.uuid}
												icon={({ className }: { className: string }) => (
													<FontAwesomeIcon icon={faCircle} className={cn('!size-2', className)} />
												)}
												className="size-5 cursor-pointer"
												checked={subbedOut?.userUuid === player.userUuid}
											/>
											<label htmlFor={player.uuid} className="cursor-pointer flex items-center gap-2">
												<Avatar>
													<AvatarImage src={determinePlayerPhotoUrl(player.photo)} />
													<AvatarFallback>
														<Icon glyph="user" className="text-gray-400 size-5" />
													</AvatarFallback>
												</Avatar>
												<p className="text-base text-gray-900">
													{formatName({ firstName: player.firstName, lastName: player.lastName })}
												</p>
											</label>
										</div>
									))}
								</RadioGroup>
								<p className="text-lg font-semibold text-gray-900">{teamOneTitle}</p>
								<RadioGroup
									className="rounded-lg border border-gray-200"
									value={subbedOut?.userUuid}
									onValueChange={(value) => {
										const player = filteredTeamOnePlayers.find((player) => player.uuid === value);
										setSubbedOut(player);
										setTeam(1);
									}}
								>
									{filteredTeamOnePlayers.map((player, index) => (
										<div
											key={player.uuid}
											className={`flex items-center gap-4 px-4 py-3 ${index % 2 === 1 ? 'bg-gray-100' : ''} ${index === teamOnePlayers.length - 1 ? 'rounded-lg' : ''}`}
										>
											<RadioGroupItem
												id={player.uuid}
												value={player.uuid}
												icon={({ className }: { className: string }) => (
													<FontAwesomeIcon icon={faCircle} className={cn('!size-2', className)} />
												)}
												className="size-5 cursor-pointer"
												checked={subbedOut?.userUuid === player.userUuid}
											/>
											<label htmlFor={player.uuid} className="cursor-pointer flex items-center gap-2">
												<Avatar>
													<AvatarImage src={determinePlayerPhotoUrl(player.photo)} />
													<AvatarFallback>
														<Icon glyph="user" className="text-gray-400 size-5" />
													</AvatarFallback>
												</Avatar>

												<p className="text-base text-gray-900">
													{formatName({ firstName: player.firstName, lastName: player.lastName })}
												</p>
											</label>
										</div>
									))}
								</RadioGroup>
							</div>
						)}
						{step === 2 && (
							<div className="mb-16 flex flex-col gap-2">
								{subbedOut ? (
									<div className="flex flex-col gap-2">
										<p className="font-medium text-gray-900">Subbing out</p>
										{renderSub(subbedOut, () => setStep(1))}
									</div>
								) : null}
								{type === 'team' &&
									(team === 1 ? teamOnePlayers : teamTwoPlayers)
										.filter((player) => player.userUuid !== subbedOut?.userUuid && player.gender === subbedOut?.gender)
										.map((player) => renderPlayer(player))}
								{type === 'event' &&
									substitutePlayers
										.filter((player) => player.userUuid !== subbedOut?.userUuid && player.gender === subbedOut?.gender)
										.map((player) => renderPlayer(player))}
								{type === 'users' && (
									<div className="flex flex-col gap-4 py-4">
										<DecoratedInput
											PrefixIcon={() => (
												<FontAwesomeIcon
													icon={faSearch}
													size="sm"
													className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
												/>
											)}
											SuffixIcon={() =>
												searchTerm && (
													<FontAwesomeIcon
														icon={faClose}
														size="sm"
														className="cursor-pointer text-gray-500 hover:text-gray-700 absolute right-3 top-1/2 -translate-y-1/2"
														onClick={(e) => {
															e.stopPropagation();
															setSearchTerm('');
														}}
													/>
												)
											}
											placeholder="Search by name"
											size={50}
											onChange={(e) => setSearchTerm(e.target.value)}
											value={searchTerm}
										/>
										{isLoadingUsers ? (
											<>
												<PlayerSelectCardSkeleton color="orange" />
												<PlayerSelectCardSkeleton color="orange" />
												<PlayerSelectCardSkeleton color="orange" />
												<PlayerSelectCardSkeleton color="orange" />
											</>
										) : null}
										{usersResponse?.data?.users
											?.filter((user) => user.uuid !== subbedOut?.userUuid && user.gender === subbedOut?.gender)
											.map((user: User) => (
												<UserCard
													key={user.uuid}
													user={user}
													add={(user: User) => setSubbedIn(user)}
													showRating
													hideButton={subbedIn?.uuid === user.uuid}
												/>
											))}
										{error ? (
											<div className="flex w-full flex-col gap-2 rounded-lg border border-gray-200 bg-white text-sm">
												<h5 className="font-bold">Error</h5>
												<p className="text-gray-500">{error.message}</p>
											</div>
										) : null}
									</div>
								)}
							</div>
						)}
						{step === 3 && (
							<div className="flex flex-col gap-6">
								{subbedOut ? (
									<div className="flex flex-col gap-2">
										<p className="font-medium text-gray-900">Subbing out</p>
										{renderSub(subbedOut, () => setStep(1))}
									</div>
								) : null}
								{subbedIn ? (
									<div className="flex flex-col gap-2">
										<p className="font-medium text-gray-900">Subbing in</p>
										{renderSub(subbedIn, () => setStep(2))}
									</div>
								) : null}

								{subbedOut?.userUuid && team ? (
									<div className="flex flex-col gap-4">
										<p className="text-lg font-semibold text-gray-900">Select matches</p>

										{getEligibleMatches(matches, subbedOut?.userUuid, team).map((match) => (
											<div className="flex items-center gap-2">
												<Checkbox
													key={match.matchUuid}
													checked={selectedMatches.includes(match.matchUuid)}
													onCheckedChange={(checked: boolean) => handleCheckboxChange(match.matchUuid, checked)}
													className="size-5 cursor-pointer rounded-md data-[state=checked]:bg-primary-100 data-[state=checked]:text-primary"
												/>
												<p className="text-gray-900">{match.roundText || ''}</p>
											</div>
										))}
									</div>
								) : null}
							</div>
						)}
						{step === 4 && (
							<div className="flex flex-col gap-6">
								{subbedOut ? (
									<div className="flex flex-col gap-2">
										<p className="font-medium text-gray-900">Subbing out</p>
										{renderSub(subbedOut, () => setStep(1))}
									</div>
								) : null}
								{subbedIn ? (
									<div className="flex flex-col gap-2">
										<p className="font-medium text-gray-900">Subbing in</p>
										{renderSub(subbedIn, () => setStep(2))}
									</div>
								) : null}
								<div className="flex flex-col gap-4">
									<div className="flex justify-between pr-3">
										<p className="text-lg font-semibold text-gray-900">Selected matches</p>
										<Button
											className="self-center text-xs font-semibold leading-[18px] text-gray-700 !border !border-gray-300 !px-2 !py-1.5 !h-fit !rounded-md"
											onClick={() => setStep(3)}
										>
											Edit
										</Button>
									</div>
									{matches
										.filter((match) => selectedMatches.includes(match.matchUuid))
										.map((match) => (
											<p key={match.matchUuid} className="text-gray-900">
												{match.roundText}
											</p>
										))}
								</div>
							</div>
						)}

						<div className="pt-20 md:pt-20">
							<div className="fixed inset-x-0 md:inset-x-auto bottom-0 z-50 flex justify-center border-t border-gray-200 bg-white w-full py-4 md:justify-start sm:pr-4 p-4 md:px-0 md:border-none">
								<div className="mr-2 flex w-full flex-col gap-2 sm:w-[525px]">
									<div className="flex flex-row gap-4 pb-4">
										<Button
											onClick={handleBack}
											className="!w-full justify-center border border-gray-300 !bg-white text-base font-semibold !text-gray-700 shadow-xs transition-all duration-200 hover:shadow-sm"
										>
											{step === 1 ? 'Cancel' : 'Back'}
										</Button>

										<Button
											onClick={handleNext}
											className={`flex w-full items-center justify-center border !text-center text-base font-semibold shadow-xs transition-all duration-200 !text-white !bg-orange-500 ${isNextStepDisabled() && 'opacity-50'}`}
											disabled={isLoadingSubmit || isNextStepDisabled()}
										>
											{step === 4 ? 'Save' : 'Next'}
										</Button>
									</div>
								</div>
							</div>
						</div>
					</div>
				</PanelBody>
			</PanelContent>
		</Panel>
	);
};
